import {
  useMemo,
  useState,
} from "react";

import {
  PRP_FORFY_AGENTS,
  PRP_FORFY_GUARDRAILS,
  type PrpForfyAgentKey,
} from "../../lib/prpForfyAgents";

import {
  prpWorkspaceCommand,
} from "../../lib/prpWorkspace";

import {
  supabase,
} from "../../lib/supabaseClient";

// LOT0F2C1_PRP_FORFY_AGENT_MEMORY_CLOSURE_V1

type AnyRecord = Record<string, unknown>;

const asText = (value: unknown, fallback = "—"): string =>
  typeof value === "string" && value.trim() ? value.trim() : fallback;

const asNumberText = (value: unknown): string => {
  const number = Number(value);
  return Number.isFinite(number) ? String(Math.round(number)) : "—";
};

const humanizeKey = (key: string): string =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

function OutputValue({ value }: { value: unknown }) {
  if (Array.isArray(value)) {
    return (
      <ul className="mt-2 space-y-1 pl-5 text-sm font-semibold text-slate-700">
        {value.map((item, index) => (
          <li className="list-disc" key={`${index}-${String(item)}`}>
            {typeof item === "object" && item !== null
              ? JSON.stringify(item)
              : String(item)}
          </li>
        ))}
      </ul>
    );
  }

  if (value && typeof value === "object") {
    return (
      <pre className="mt-2 overflow-auto rounded-xl bg-slate-950 p-3 text-xs font-semibold text-slate-100">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  return (
    <p className="mt-2 whitespace-pre-wrap text-sm font-semibold leading-6 text-slate-700">
      {String(value ?? "—")}
    </p>
  );
}

export default function PrpForfyAgentsPanel() {
  const [selectedAgentKey, setSelectedAgentKey] =
    useState<PrpForfyAgentKey>("forfy_prp_analyst");

  const [businessName, setBusinessName] = useState("FORFY INC.");
  const [city, setCity] = useState("QUEBEC");
  const [category, setCategory] = useState("Spa");
  const [status, setStatus] = useState("enrichissement requis");
  const [contactEmail, setContactEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState<AnyRecord | null>(null);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<AnyRecord | null>(null);

  const selectedAgent =
    useMemo(
      () =>
        PRP_FORFY_AGENTS.find((agent) => agent.key === selectedAgentKey)
        || PRP_FORFY_AGENTS[0],
      [selectedAgentKey],
    );

  const output =
    result?.output && typeof result.output === "object" && !Array.isArray(result.output)
      ? result.output as AnyRecord
      : null;

  const runtimeMeta =
    result?.runtime_meta
      && typeof result.runtime_meta === "object"
      && !Array.isArray(result.runtime_meta)
      ? result.runtime_meta as AnyRecord
      : null;

  const resetResult = () => {
    setResult(null);
    setSaveState(null);
  };

  const runServerAgent = async () => {
    setRunning(true);
    setResult(null);
    setSaveState(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        setResult({
          ok: false,
          error: "Session PRP absente. Reconnecte-toi avant d’exécuter Forfy.",
        });
        setRunning(false);
        return;
      }

      const supabaseUrl =
        (import.meta as any).env?.VITE_SUPABASE_URL as string | undefined;

      if (!supabaseUrl) {
        setResult({
          ok: false,
          error: "Configuration serveur Explore indisponible.",
        });
        setRunning(false);
        return;
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/prp-forfy-agent-run`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action: "run_prp_forfy_agent",
            agent_key: selectedAgent.key,
            context: {
              business_name: businessName,
              city,
              region: "Capitale-Nationale",
              category,
              status,
              contact_email: contactEmail,
              notes,
              source: "prp_dashboard_forfy_agents",
            },
          }),
        },
      );

      const raw = await response.text();
      let parsed: AnyRecord = {};

      try {
        parsed = raw ? JSON.parse(raw) : {};
      } catch {
        parsed = {
          ok: false,
          error: "Réponse serveur Forfy invalide.",
        };
      }

      setResult({
        http_status: response.status,
        ...parsed,
      });
    } catch (error) {
      setResult({
        ok: false,
        error:
          error instanceof Error
            ? error.message
            : "Forfy n’a pas pu terminer cette analyse.",
      });
    } finally {
      setRunning(false);
    }
  };

  const saveForfyResult = async () => {
    if (!output || result?.ok !== true || saving) {
      return;
    }

    const forfyResultId = asText(result.forfy_result_id, "");
    if (!forfyResultId) {
      setSaveState({
        ok: false,
        error: "Identifiant du brouillon Forfy absent. Relance l’analyse avant de conserver.",
      });
      return;
    }

    setSaving(true);
    setSaveState(null);

    try {
      const saved = await prpWorkspaceCommand(
        "forfy_result_save",
        {
          forfy_result_id: forfyResultId,
          agent_key: selectedAgent.key,
          business_name: businessName,
          output,
          runtime_meta: runtimeMeta || {},
          canonical_product_context_version:
            asText(result.canonical_product_context_version, "forfeo-products-v1"),
        },
      );

      setSaveState({
        ok: true,
        ...saved.result,
      });
    } catch (error) {
      setSaveState({
        ok: false,
        error:
          error instanceof Error
            ? error.message
            : "Impossible de conserver cette analyse dans le CRM PRP.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
      data-lot0f2c-prp-forfy-agent-memory="true"
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs font-black uppercase tracking-[0.28em] text-emerald-700">
            Forfy Agents PRP
          </p>
          <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-black text-violet-800">
            Moteur serveur Forfy
          </span>
          <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-black text-sky-800">
            Mémoire CRM sur validation
          </span>
        </div>

        <h3 className="text-lg font-black text-slate-950">
          Équipe Forfy dérivée de Pulse
        </h3>

        <p className="text-sm font-semibold text-slate-600">
          Choisis un agent spécialisé. Forfy prépare une analyse serveur; rien n’est sauvegardé automatiquement.
          Après lecture, le PRP peut explicitement conserver le brouillon dans le journal CRM.
        </p>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-4">
        <label className="flex flex-col gap-1 text-sm font-bold text-slate-700">
          Commerce
          <input
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900"
            value={businessName}
            onChange={(event) => {
              setBusinessName(event.target.value);
              resetResult();
            }}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm font-bold text-slate-700">
          Ville
          <input
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900"
            value={city}
            onChange={(event) => {
              setCity(event.target.value);
              resetResult();
            }}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm font-bold text-slate-700">
          Catégorie
          <input
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900"
            value={category}
            onChange={(event) => {
              setCategory(event.target.value);
              resetResult();
            }}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm font-bold text-slate-700">
          Statut
          <input
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              resetResult();
            }}
          />
        </label>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-[1fr_2fr]">
        <label className="flex flex-col gap-1 text-sm font-bold text-slate-700">
          Courriel prospect — optionnel
          <input
            className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-900"
            type="email"
            value={contactEmail}
            onChange={(event) => {
              setContactEmail(event.target.value);
              resetResult();
            }}
            placeholder="contact@commerce.ca"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm font-bold text-slate-700">
          Notes PRP — optionnel
          <textarea
            className="min-h-24 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900"
            value={notes}
            onChange={(event) => {
              setNotes(event.target.value);
              resetResult();
            }}
            placeholder="Ex. rencontré sur place, intéressé par une offre expérience, rappeler jeudi…"
          />
        </label>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {PRP_FORFY_AGENTS.map((agent) => (
          <button
            className={`rounded-2xl border p-4 text-left transition ${
              selectedAgent.key === agent.key
                ? "border-emerald-300 bg-emerald-50"
                : "border-slate-100 bg-slate-50 hover:border-slate-300"
            }`}
            key={agent.key}
            onClick={() => {
              setSelectedAgentKey(agent.key);
              resetResult();
            }}
            type="button"
          >
            <p className="text-sm font-black text-slate-950">
              {agent.name}
            </p>
            <p className="mt-1 text-xs font-black uppercase tracking-[0.18em] text-slate-400">
              {agent.pulseAgentName} · {agent.pulseSquad}
            </p>
            <p className="mt-2 text-sm font-semibold text-slate-600">
              {agent.description}
            </p>
            <p className="mt-3 text-xs font-black text-slate-500">
              Validation humaine requise
            </p>
          </button>
        ))}
      </div>

      <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-black text-slate-950">
              {selectedAgent.name}
            </p>
            <p className="text-sm font-semibold text-slate-600">
              {selectedAgent.recommendedUse}
            </p>
            <p className="mt-2 text-xs font-black uppercase tracking-[0.18em] text-slate-400">
              Sortie : {selectedAgent.outputContract.join(" · ")}
            </p>
          </div>

          <button
            className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white disabled:cursor-wait disabled:opacity-50"
            disabled={running || !businessName.trim()}
            onClick={runServerAgent}
            type="button"
          >
            {running ? "Forfy analyse…" : "Exécuter Forfy"}
          </button>
        </div>

        {result && (
          <div className="mt-5">
            {result.ok === true && output ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-black text-emerald-800">
                    Analyse Forfy terminée
                  </span>
                  {result.fallback_used === true && (
                    <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-black text-slate-700">
                      Secours serveur utilisé
                    </span>
                  )}
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-slate-600">
                    Vocabulaire Forfeo verrouillé
                  </span>
                </div>

                {runtimeMeta && (
                  <div className="mt-4 rounded-2xl border border-violet-100 bg-violet-50 p-4">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-violet-800">
                      Observabilité serveur
                    </p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                      <div>
                        <p className="text-xs font-bold text-slate-500">Moteur primaire</p>
                        <p className="text-sm font-black text-slate-900">
                          {asText(runtimeMeta.provider_primary)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-500">Moteur utilisé</p>
                        <p className="text-sm font-black text-slate-900">
                          {asText(runtimeMeta.provider_used)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-500">Modèle</p>
                        <p className="text-sm font-black text-slate-900">
                          {asText(runtimeMeta.model_used)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-500">Latence</p>
                        <p className="text-sm font-black text-slate-900">
                          {asNumberText(runtimeMeta.latency_ms)} ms
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-500">Cause du secours</p>
                        <p className="text-sm font-black text-slate-900">
                          {runtimeMeta.fallback_used === true
                            ? asText(runtimeMeta.fallback_reason, "secours utilisé")
                            : "aucun"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {Object.entries(output).map(([key, value]) => (
                    <div className="rounded-2xl border border-slate-100 bg-white p-4" key={key}>
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">
                        {humanizeKey(key)}
                      </p>
                      <OutputValue value={value} />
                    </div>
                  ))}
                </div>

                <div
                  className="relative mt-4 scroll-mb-32 rounded-2xl border border-sky-100 bg-sky-50 p-4 pb-6"
                  data-lot0f2c1-crm-memory-action="true"
                >
                  <div>
                    <p className="text-sm font-black text-sky-950">
                      Mémoire CRM
                    </p>
                    <p className="text-sm font-semibold text-sky-800">
                      Rien n’est enregistré automatiquement. Conserve seulement les analyses utiles après lecture.
                    </p>
                  </div>

                  <button
                    className="mt-3 w-full rounded-2xl bg-sky-900 px-5 py-3 text-sm font-black text-white disabled:cursor-wait disabled:opacity-50 sm:w-auto"
                    disabled={saving || saveState?.duplicate === true}
                    onClick={saveForfyResult}
                    type="button"
                  >
                    {saving
                      ? "Conservation…"
                      : saveState?.duplicate === true
                        ? "Déjà conservé — aucun doublon"
                        : saveState?.ok === true
                          ? "Vérifier la déduplication"
                          : "Conserver dans le CRM"}
                  </button>

                  {saveState?.ok === true && (
                    <div
                      className="mt-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-4"
                      data-lot0f2c1-crm-save-confirmation="true"
                    >
                      <p className="text-sm font-black text-emerald-900">
                        {saveState.duplicate === true
                          ? "✓ Déjà conservé — aucun doublon créé"
                          : "✓ Conservé dans le CRM"}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-emerald-800">
                        {saveState.duplicate === true
                          ? "La même analyse Forfy était déjà présente dans le journal CRM."
                          : "Analyse conservée comme activité CRM après confirmation humaine."}
                        {saveState.target_resolution === "portfolio_unresolved"
                          ? " Le commerce n’a pas été relié automatiquement à une fiche unique; l’activité reste visible au niveau du portefeuille PRP."
                          : ""}
                      </p>
                    </div>
                  )}

                  {saveState && saveState.ok !== true && (
                    <div className="mt-3 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm font-bold text-rose-800">
                      {asText(saveState.error, "Impossible de conserver cette analyse.")}
                    </div>
                  )}
                </div>

                <p className="mt-4 text-xs font-bold text-slate-500">
                  Brouillon assistif. Aucune action externe n’a été exécutée.
                </p>
              </>
            ) : (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                <p className="text-sm font-black text-rose-900">
                  Forfy n’a pas pu exécuter cet agent.
                </p>
                <p className="mt-1 text-sm font-semibold text-rose-800">
                  {result.configuration_required === true
                    ? "Le moteur IA serveur doit être configuré dans les secrets Supabase avant l’exécution réelle."
                    : asText(result.error, "Erreur serveur Forfy.")}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm font-black text-amber-950">
          Garde-fous
        </p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-amber-900">
          {PRP_FORFY_GUARDRAILS.map((item) => (
            <li key={item}>• {item}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}
